/**
 * Hook for fetching today's schedule data
 * Combines calendar events and tasks due today into a unified timeline
 */

import { useMemo } from 'react'
import { calendarApi } from '@/lib/api/calendar.api'
import { findConferenceUrl } from '@/lib/utils/conference-links'
// Store-based auth (not the AuthProvider context): this hook also runs in
// the Benny companion window, which has no AuthProvider. The store mirrors
// the context user in the main window (synced by MainLayout).
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import {
  CalendarExternalEvent,
  CalendarMeetingEvent,
  CalendarTask,
  TaskStatusTypeEnum,
  TaskWithAssignments,
} from '@scrambl/shared'

import { useMyTasksData } from './useMyTasksData'

export type ScheduleItemType = 'task' | 'event'
export type ScheduleItemSource =
  | 'task'
  | 'event'
  | 'google_calendar'
  | 'outlook_calendar'

export interface ScheduleItem {
  id: string
  type: ScheduleItemType
  source: ScheduleItemSource
  title: string
  startTime: Date
  endTime?: Date
  durationMinutes?: number
  allDay: boolean
  color?: string
  // Task-specific fields
  taskId?: string
  taskNumber?: string
  status?: string
  statusTypeId?: string
  priority?: string
  projectName?: string
  /** Record the task lives under (usually a project) — links projectName. */
  projectModuleType?: string
  projectModuleId?: string
  entityName?: string
  entityId?: string
  /**
   * Task-type summary for icon display (personal "meals/activities" lens).
   * Only populated for my-task-sourced items — the calendar API's task
   * payload doesn't carry the type summary, and we don't widen its calls
   * for this.
   */
  taskType?: {
    name: string
    icon?: string | null
    color?: string | null
    displayStyle?: string | null
  }
  // Event-specific fields
  location?: string
  calendarName?: string
  htmlLink?: string
  description?: string
  /** Video-call join URL, when the event has one. */
  conferenceUrl?: string
  conferenceProvider?: string
  attendees?: { name?: string; email?: string; responseStatus?: string }[]
  /** Scrambl Event id — set when the item is already a Scrambl meeting
      record, so time can be logged against it directly. */
  eventId?: string
  /** Raw external calendar event — set for unpromoted Google/Outlook items
      so surfaces can promote them into the events module. */
  externalEvent?: CalendarExternalEvent
}

export interface TodayScheduleSummary {
  totalEvents: number
  totalTasks: number
  completedTasks: number
  upcomingItems: number
}

export interface UseTodayScheduleDataOptions {
  enabled?: boolean
  staleTime?: number
  includeExternalEvents?: boolean
}

export interface UseTodayScheduleDataResult {
  items: ScheduleItem[]
  /** Tomorrow's schedule — surfaced beneath today's timeline, and in place
      of the empty state when today has nothing scheduled. */
  tomorrowItems: ScheduleItem[]
  summary: TodayScheduleSummary
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refresh: () => Promise<void>
}

// Format date as YYYY-MM-DD for API
function formatDateForApi(date: Date): string {
  return date.toISOString().split('T')[0]
}

// Transform calendar task to schedule item
function calendarTaskToScheduleItem(task: CalendarTask): ScheduleItem {
  const start = new Date(task.start)
  const end = task.end ? new Date(task.end) : undefined
  const durationMinutes = end
    ? Math.round((end.getTime() - start.getTime()) / (1000 * 60))
    : undefined

  return {
    id: `calendar-task-${task.id}`,
    type: 'task',
    source: 'task',
    title: task.title,
    startTime: start,
    endTime: end,
    durationMinutes,
    allDay: task.allDay,
    color: task.color,
    taskId: task.id,
    taskNumber: task.taskNumber,
    status: task.status,
    statusTypeId: task.statusTypeId,
    priority: task.priority,
    projectName: task.projectName,
    projectModuleType: task.projectId ? 'PROJECT' : undefined,
    projectModuleId: task.projectId,
    entityName: task.entityName,
    entityId: task.entityId,
  }
}

// Transform external event to schedule item
function externalEventToScheduleItem(
  event: CalendarExternalEvent,
): ScheduleItem {
  const start = new Date(event.start)
  const end = event.end ? new Date(event.end) : undefined
  const durationMinutes = end
    ? Math.round((end.getTime() - start.getTime()) / (1000 * 60))
    : undefined

  return {
    id: `event-${event.externalId}`,
    type: 'event',
    source: event.type as ScheduleItemSource,
    title: event.title,
    startTime: start,
    endTime: end,
    durationMinutes,
    allDay: event.allDay,
    color: event.color,
    location: event.location,
    calendarName: event.calendarName,
    htmlLink: event.htmlLink,
    description: event.description,
    // Providers usually give an explicit join URL; fall back to sniffing the
    // location/description, where organizers often paste it.
    conferenceUrl:
      event.conferenceUrl ??
      findConferenceUrl(event.location, event.description),
    conferenceProvider: event.conferenceProvider,
    attendees: event.attendees?.map(attendee => ({
      name: attendee.name,
      email: attendee.email,
      responseStatus: attendee.responseStatus,
    })),
    externalEvent: event,
  }
}

function meetingEventToScheduleItem(event: CalendarMeetingEvent): ScheduleItem {
  const start = new Date(event.start)
  const end = event.end ? new Date(event.end) : undefined
  const durationMinutes = end
    ? Math.round((end.getTime() - start.getTime()) / (1000 * 60))
    : undefined

  return {
    id: `meeting-${event.eventId}`,
    type: 'event',
    source: 'event',
    title: event.title,
    startTime: start,
    endTime: end,
    durationMinutes,
    allDay: event.allDay,
    color: event.color,
    location: event.location,
    htmlLink: event.htmlLink,
    description: event.description,
    // Internal events carry no conferenceUrl field — organizers paste the
    // join link into the location or description.
    conferenceUrl: findConferenceUrl(event.location, event.description),
    attendees: event.attendees?.map(invitee => ({
      name: invitee.name,
      email: invitee.email,
      responseStatus: invitee.status,
    })),
    eventId: event.eventId,
  }
}

// Transform my task to schedule item (for tasks due today without specific time)
function myTaskToScheduleItem(task: TaskWithAssignments): ScheduleItem {
  const dueDate = task.dueDate ? new Date(task.dueDate) : new Date()

  return {
    id: `my-task-${task.id}`,
    type: 'task',
    source: 'task',
    title: task.name,
    startTime: dueDate,
    endTime: undefined,
    durationMinutes: undefined,
    allDay: true,
    taskId: task.id,
    taskNumber: task.taskNumber || undefined,
    status: task.status,
    statusTypeId: task.statusTypeId,
    priority: task.priority,
    projectName: task.moduleInfo?.name,
    projectModuleType: task.moduleInfo?.type,
    projectModuleId: task.moduleInfo?.id,
    entityName: task.moduleInfo?.entity?.name,
    entityId: task.moduleInfo?.entity?.id,
    taskType: task.taskTypeDef
      ? {
          name: task.taskTypeDef.name,
          icon: task.taskTypeDef.icon,
          color: task.taskTypeDef.color,
          displayStyle: task.taskTypeDef.displayStyle,
        }
      : undefined,
  }
}

export const TODAY_SCHEDULE_QUERY_KEY = ['today-schedule'] as const

export interface UseTodayCalendarEventsOptions {
  enabled?: boolean
  staleTime?: number
  includeExternalEvents?: boolean
}

/**
 * Today + tomorrow calendar events (tasks, promoted meetings, external
 * events) as one shared query. The Overview/Today schedule widgets and the
 * timer's Link Work event suggestions read the same cache entry, so a user
 * who came through the Today page opens the timer picker with warm data
 * instead of triggering another live provider fetch.
 */
export function useTodayCalendarEventsQuery(
  options: UseTodayCalendarEventsOptions = {},
) {
  const {
    enabled = true,
    staleTime = 60000,
    includeExternalEvents = true,
  } = options

  const { user } = useAuth()
  const currentUserId = user?.id
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )

  // Fetch today + tomorrow in one range; consumers split by day so the
  // widgets can show tomorrow's schedule under (or instead of) today's.
  const range = useMemo(() => {
    const now = new Date()
    const start = new Date(now)
    start.setHours(0, 0, 0, 0)

    const end = new Date(now)
    end.setDate(end.getDate() + 1)
    end.setHours(23, 59, 59, 999)

    return {
      startDate: formatDateForApi(start),
      endDate: formatDateForApi(end),
    }
  }, [])

  return useQuery({
    queryKey: [
      ...TODAY_SCHEDULE_QUERY_KEY,
      'calendar',
      organizationId,
      range.startDate,
      range.endDate,
      currentUserId,
      includeExternalEvents,
    ],
    queryFn: async () => {
      const response = await calendarApi.getEvents({
        startDate: range.startDate,
        endDate: range.endDate,
        includeTasks: true,
        includeExternalEvents,
        assigneeIds: currentUserId ? [currentUserId] : undefined,
      })
      return response
    },
    enabled: enabled && !!currentUserId && !!organizationId,
    staleTime,
  })
}

export function useTodayScheduleData(
  options: UseTodayScheduleDataOptions = {},
): UseTodayScheduleDataResult {
  const {
    enabled = true,
    staleTime = 60000,
    includeExternalEvents = true,
  } = options

  const { user } = useAuth()
  const currentUserId = user?.id
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const queryClient = useQueryClient()

  // Query for calendar events (shared with the timer's Link Work picker)
  const calendarQuery = useTodayCalendarEventsQuery({
    enabled,
    staleTime,
    includeExternalEvents,
  })

  // Also get my tasks for today (in case calendar API doesn't return all)
  const { tasks: myTasks, isLoading: myTasksLoading } = useMyTasksData({
    enabled: enabled && !!currentUserId && !!organizationId,
    staleTime,
    includeCompleted: false,
    reuseCompletedList: true,
  })

  // Combine, split by day, and sort items
  const { items, tomorrowItems, summary } = useMemo(() => {
    const now = new Date()
    const todayStart = new Date(now)
    todayStart.setHours(0, 0, 0, 0)
    const tomorrowStart = new Date(todayStart)
    tomorrowStart.setDate(tomorrowStart.getDate() + 1)
    const tomorrowEnd = new Date(todayStart)
    tomorrowEnd.setDate(tomorrowEnd.getDate() + 2)

    const allItems: ScheduleItem[] = []
    const calendarTaskIds = new Set<string>()

    // Add calendar tasks
    const calendarTasks = calendarQuery.data?.tasks || []
    calendarTasks.forEach(task => {
      allItems.push(calendarTaskToScheduleItem(task))
      calendarTaskIds.add(task.id)
    })

    // Add external events
    const externalEvents = calendarQuery.data?.externalEvents || []
    externalEvents.forEach(event => {
      allItems.push(externalEventToScheduleItem(event))
    })

    const promotedEvents = calendarQuery.data?.events || []
    promotedEvents.forEach(event => {
      allItems.push(meetingEventToScheduleItem(event))
    })

    // Add my tasks due today or tomorrow that aren't already in calendar
    myTasks.forEach(task => {
      // Skip if already included from calendar
      if (calendarTaskIds.has(task.id)) return

      // Skip completed/canceled tasks
      if (
        task.statusTypeId === TaskStatusTypeEnum.COMPLETED ||
        task.statusTypeId === TaskStatusTypeEnum.CANCELED
      ) {
        return
      }

      // Check if task is due today or tomorrow
      if (!task.dueDate) return
      const dueDate = new Date(task.dueDate)
      dueDate.setHours(0, 0, 0, 0)

      if (
        dueDate.getTime() === todayStart.getTime() ||
        dueDate.getTime() === tomorrowStart.getTime()
      ) {
        allItems.push(myTaskToScheduleItem(task))
      }
    })

    // Sort by start time (all-day items at the top, then by time)
    allItems.sort((a, b) => {
      // All-day items first
      if (a.allDay && !b.allDay) return -1
      if (!a.allDay && b.allDay) return 1

      // Then by start time
      return a.startTime.getTime() - b.startTime.getTime()
    })

    // Split the fetched range at local midnight; anything starting before
    // tomorrow stays on today's timeline (matches the prior behavior). The
    // UTC-based API range can spill past tomorrow, so bound the upper end too.
    const todayItems = allItems.filter(item => item.startTime < tomorrowStart)
    const tomorrowItems = allItems.filter(
      item => item.startTime >= tomorrowStart && item.startTime < tomorrowEnd,
    )

    // Calculate summary (today only — the briefing sentence reads from it)
    const taskItems = todayItems.filter(item => item.type === 'task')
    const eventItems = todayItems.filter(item => item.type === 'event')
    const completedTasks = taskItems.filter(
      item => item.statusTypeId === TaskStatusTypeEnum.COMPLETED,
    )
    const upcomingItems = todayItems.filter(item => item.startTime > now)

    const summary: TodayScheduleSummary = {
      totalEvents: eventItems.length,
      totalTasks: taskItems.length,
      completedTasks: completedTasks.length,
      upcomingItems: upcomingItems.length,
    }

    return { items: todayItems, tomorrowItems, summary }
  }, [calendarQuery.data, myTasks])

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: TODAY_SCHEDULE_QUERY_KEY })
  }

  return {
    items,
    tomorrowItems,
    summary,
    isLoading: calendarQuery.isLoading || myTasksLoading,
    isFetching: calendarQuery.isFetching,
    error: calendarQuery.error as Error | null,
    refresh,
  }
}
